import { useState } from 'react';
import { Drawer } from '@mantine/core';

import styles from './MeetingModal.module.scss';
import { ReactComponent as BlackBackIcon } from "assets/blackBack.svg";
import { ReactComponent as IdentificationCardIcon } from "assets/identificationCard.svg";
import { ReactComponent as NavigationArrowIcon } from "assets/navigationArrowRight.svg";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { ReactComponent as AlarmIcon } from "assets/alarm.svg";
import { ReactComponent as BlueAlarmIcon } from "assets/blueAlarm.svg";
import StartTimeModal from '../StartTimeModal';
import DurationModal from '../DurationModal';
import { store } from "stores/Store";
import BaseballCard, { Tab } from 'Modules/PractitionerBaseballCard';
import useWindowSize from 'hooks/useWindowSize';
import { getLeadStateByPractitioner } from 'helpers/lead';

interface IMeetingModalProps {
  isOpen: boolean;
  practitioner: any;
  onClose: () => void;
  updatePractitioner: (practitionerId: number, updateData: any) => void;
  checkDuration: () => string;
  checkStartTime: () => string;
  setGoRouteModalIsOpen: (isOpen: boolean) => void;
}

const MeetingModal = ({ isOpen, practitioner, onClose, updatePractitioner, checkDuration, checkStartTime, setGoRouteModalIsOpen }: IMeetingModalProps) => {
  const [startTime, setStartTime] = useState(checkStartTime());
  const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
  const [duration, setDuration] = useState<string>(checkDuration() ? checkDuration() : '15 min');
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
  const [isIdentificationCardModalOpen, setIsIdentificationCardModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { width, height } = useWindowSize();
  const closeMeetingModal = () => {
    onClose();
    const minutes = duration.includes('min')
      ? parseInt(duration)
      : Math.round(parseFloat(duration) * 60);
    updatePractitioner(practitioner.id, {
      start_time: startTime,
      meeting_duration: minutes
    });
    setStartTime('');
    setDuration('15 min');
  }

  const handleIdentificationCardClick = () => {
    store.setCurrentPractitioner(practitioner);
    setIsIdentificationCardModalOpen(true);
  }

  if (!isOpen || !practitioner) return null;

  return (
    <>
      <Drawer
        opened
        onClose={onClose}
        position="bottom"
        withCloseButton={false}
        classNames={{
          overlay: styles.drawerOverlay,
          inner: styles.drawerInner,
          content: styles.drawerContent,
        }}
      >
        <div className={styles.header}>
          <BlackBackIcon
            className={styles.backButton}
            onClick={closeMeetingModal}
            height={20}
            width={20}
            style={{
              position: 'absolute',
              left: 15,
              paddingRight: 10,
              top: 30
            }}

          />
          <div className={styles.practitionerInfo}>
            <div className={styles.practitionerInfoHeader}>
              <span className={styles.practitionerName}>{practitioner.name}</span>
              <span className={styles.practitionerSpecialty}>{practitioner.specialty}</span>
            </div>
            <IdentificationCardIcon onClick={handleIdentificationCardClick} style={{ cursor: 'pointer', marginRight: '7px' }} />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.address}>
            {practitioner.address}
            <div className={styles.navigateButton} onClick={() => setGoRouteModalIsOpen(true)}>
              <NavigationArrowIcon stroke={'#4F7D9C'} width={22} height={22} />NAVIGATE
            </div>
          </div>
          <div className={styles.timeWrapper}>
            <div className={styles.timeHeader}>
              <span style={{ fontSize: 16 }}>
                Start Time
              </span>
              <span className={styles.timeHeaderHelper}>
                If you need to be at the location at this time
              </span>
            </div>
            <div>
              {startTime ? (
                <div className={styles.time}>
                  <BlueAlarmIcon width={35} height={35} />
                  <span onClick={() => setIsStartTimeModalOpen(true)} style={{ fontSize: 30, color: "#4F7D9C" }}>{startTime}</span>
                  <CloseIcon style={{ marginLeft: 20 }} color="#4F7D9C" width={20} height={20} onClick={() => setStartTime('')} />
                </div>
              ) : (
                <div onClick={() => setIsStartTimeModalOpen(true)} className={styles.timeComponent}>
                  <AlarmIcon />
                  <span style={{ fontSize: 16, color: "#8B8B8B" }} >No time set</span>
                </div>
              )}
              <StartTimeModal
                isOpen={isStartTimeModalOpen}
                onClose={() => setIsStartTimeModalOpen(false)}
                startTime={startTime}
                setStartTime={setStartTime}
                setIsStartTimeModalOpen={setIsStartTimeModalOpen}
              />
            </div>
          </div>
          <span style={{ paddingLeft: 10, fontSize: 16 }}>
            Meeting Duration
          </span>
          <div className={styles.duration}>
            <div onClick={() => setIsDurationModalOpen(true)} className={styles.timeComponent}>
              <span style={{ fontSize: 30 }}>
                {duration.includes('min')
                  ? duration.split(' ')[0]
                  : Number(duration.split(' ')[0]).toString()}
              </span>
              <span style={{ fontSize: 18 }}>
                {duration.includes('min') ? 'Minutes' : parseFloat(duration) === 1.0 ? 'Hour' : 'Hours'}
              </span>
            </div>
            <DurationModal
              isOpen={isDurationModalOpen}
              onClose={() => {
                setIsDurationModalOpen(false);
              }}
              duration={duration}
              setDuration={setDuration}
              setIsDurationModalOpen={setIsDurationModalOpen}
            />
          </div>
        </div>
      </Drawer>
      <Drawer
        opened={isIdentificationCardModalOpen}
        onClose={onClose}
        position="bottom"
        withCloseButton={false}
        classNames={{
          overlay: styles.drawerOverlay,
          inner: styles.drawerInner,
          content: styles.drawerContent,
        }}
      >
        <div
          className={styles.baseballCardModalContent}
          style={{ width: width > 480 ? width * .6 : width * .9, height: height * .8, position: 'relative', paddingTop: "40px", }}
        >
          <div className={getLeadStateByPractitioner(store.currentPractitioner) ? styles["tinted-border"] : ""}>
            {getLeadStateByPractitioner(store.currentPractitioner) && (
              <div className={styles["top-message"]}>
                {getLeadStateByPractitioner(store.currentPractitioner)}
              </div>
            )}
          </div>
          <BaseballCard backHandler={() => setIsIdentificationCardModalOpen(false)} defaultTab={Tab.SUMMARY} generalNotes={practitioner.general_notes || ''} />
        </div>
      </Drawer>
    </>
  );
};

export default MeetingModal;
